// Grid insights: the "read it out loud" engine.
//
// Turns a live grid state (carbon-intensity band, generation mix, forecast and
// the reader's local hour) into a single plain-English headline, an actionable
// nudge and the next green window. Deliberately speaks in shares and bands, not
// gigawatts, the Carbon Intensity API only gives generation as percentages, and
// homeowners don't think in GW.
//
// Pure and side-effect free so it can be unit-tested in node and reused by the
// Alpine store. Never hardcode gCO2 thresholds: colour and band come from the
// API's own `index` string, which NESO quietly re-tightens each year.

// Map the API's five-band index onto a three-colour traffic light.
const BAND_TO_TRAFFIC = {
    'very low': 'green',
    low: 'green',
    moderate: 'amber',
    high: 'red',
    'very high': 'red',
};

// Fuels the Carbon Intensity API reports as a share of the mix.
const RENEWABLE_FUELS = ['wind', 'solar', 'hydro'];
const ZERO_CARBON_FUELS = ['wind', 'solar', 'hydro', 'nuclear'];

export function trafficFor(index) {
    return BAND_TO_TRAFFIC[String(index || '').toLowerCase()] || 'amber';
}

export function shareOf(mix, fuel) {
    if (!Array.isArray(mix)) return 0;
    const row = mix.find(item => item && item.fuel === fuel);
    const perc = row ? Number(row.perc) : 0;
    return Number.isFinite(perc) ? perc : 0;
}

export function sumShares(mix, fuels) {
    return fuels.reduce((total, fuel) => total + shareOf(mix, fuel), 0);
}

// Order the mix clean-first so the bar tells the green story left to right.
export const MIX_ORDER = ['solar', 'wind', 'hydro', 'nuclear', 'biomass', 'imports', 'gas', 'other', 'coal'];

export function orderedMix(mix) {
    if (!Array.isArray(mix)) return [];
    const byFuel = new Map(mix.map(item => [item.fuel, Number(item.perc) || 0]));
    return MIX_ORDER
        .filter(fuel => byFuel.has(fuel))
        .map(fuel => ({ fuel, perc: byFuel.get(fuel) }));
}

// The headline rules, in priority order: the first that matches wins. Each is a
// warm, specific, non-preachy sentence grounded in a real data condition.
// A natural-language list of the fuels actually present (share > 1%) in a group,
// so we never name a source that isn't contributing this half-hour.
const FUEL_WORD = { wind: 'wind', solar: 'sun', hydro: 'water', nuclear: 'nuclear', biomass: 'plant matter' };
function fuelClause(mix, fuels) {
    const present = fuels.filter(f => shareOf(mix, f) > 1).map(f => FUEL_WORD[f] || f);
    if (present.length === 0) return 'clean sources';
    if (present.length === 1) return present[0];
    if (present.length === 2) return `${present[0]} and ${present[1]}`;
    return `${present.slice(0, -1).join(', ')} and ${present[present.length - 1]}`;
}

// The headline rules, in priority order: the first that matches wins. Every rule
// asserts only what the mix + band guarantee — no weather, demand or timing the
// widget can't observe.
const HEADLINE_RULES = [
    {
        key: 'wind-dominant',
        test: s => shareOf(s.mix, 'wind') > 60,
        line: () => "Wind alone is doing most of the grid's heavy lifting right now.",
    },
    {
        key: 'wind-majority',
        test: s => shareOf(s.mix, 'wind') > 50,
        line: () => "Right now, more than half of Britain's electricity is coming from the wind.",
    },
    {
        key: 'near-zero-carbon',
        test: s => sumShares(s.mix, ZERO_CARBON_FUELS) > 95,
        line: () => "The grid is almost entirely carbon-free this half-hour, this is what a clean electricity system looks like.",
    },
    {
        key: 'zero-carbon-high',
        test: s => sumShares(s.mix, ZERO_CARBON_FUELS) > 80,
        line: s => `More than four-fifths of the grid is running carbon-free right now, ${fuelClause(s.mix, ZERO_CARBON_FUELS)} doing the work.`,
    },
    {
        key: 'solar-peak',
        test: s => shareOf(s.mix, 'solar') > 25 && s.localHour >= 10 && s.localHour <= 15,
        line: () => "The sun's doing the heavy lifting, solar panels across the country are pouring power into the grid right now.",
    },
    {
        key: 'renewables-majority',
        test: s => sumShares(s.mix, RENEWABLE_FUELS) > 50,
        line: s => `Over half your electricity is renewable right now, made by nothing but ${fuelClause(s.mix, RENEWABLE_FUELS)}.`,
    },
    {
        key: 'very-green',
        test: s => s.traffic === 'green',
        line: () => "Britain's running clean right now, the grid is about as green as it usually gets.",
    },
    {
        key: 'evening-peak',
        test: s => s.traffic === 'red' && s.localHour >= 16 && s.localHour <= 19 && !s.isWeekend,
        line: () => "It's the evening peak, and the grid's leaning more on gas right now.",
    },
    {
        key: 'gas-heavy',
        test: s => s.traffic === 'red',
        line: () => "Gas is doing more of the work right now, so the grid's a little dirtier than usual.",
    },
    {
        key: 'middling',
        test: () => true,
        line: s => shareOf(s.mix, 'solar') > 15
            ? "The grid's about middling right now, solar's helping, but there's still gas in the mix."
            : "The grid's about middling right now, a mix of clean power and gas keeping the lights on.",
    },
];

// Find the greenest upcoming half-hour from the 48h forecast series. Each entry
// is { from, to, intensity: { forecast } } as returned by /intensity/.../fw24h.
export function nextGreenWindow(forecastSeries, { fromTime, toLocalLabel } = {}) {
    if (!Array.isArray(forecastSeries) || forecastSeries.length === 0) return null;
    const now = fromTime instanceof Date ? fromTime.getTime() : Date.now();
    let best = null;
    for (const entry of forecastSeries) {
        const value = Number(entry?.intensity?.forecast);
        if (!Number.isFinite(value)) continue;
        const start = new Date(entry.from);
        if (Number.isNaN(start.getTime()) || start.getTime() <= now) continue;
        if (!best || value < best.value) best = { value, start, entry };
    }
    if (!best) return null;
    return {
        value: best.value,
        at: best.start,
        atLabel: typeof toLocalLabel === 'function' ? toLocalLabel(best.start) : best.start.toISOString(),
    };
}

// The main entry point. `state` shape:
//   { intensity: { actual, forecast, index }, mix: [{fuel, perc}],
//     localHour: 0-23, isWeekend: bool }
export function readGrid(state = {}) {
    const intensity = state.intensity || {};
    const band = String(intensity.index || '').toLowerCase() || 'moderate';
    const traffic = trafficFor(band);
    const resolved = {
        mix: Array.isArray(state.mix) ? state.mix : [],
        localHour: Number.isFinite(state.localHour) ? state.localHour : new Date().getHours(),
        isWeekend: Boolean(state.isWeekend),
        traffic,
        band,
    };

    const rule = HEADLINE_RULES.find(candidate => {
        try {
            return candidate.test(resolved);
        } catch {
            return false;
        }
    }) || HEADLINE_RULES[HEADLINE_RULES.length - 1];

    return {
        band,
        traffic,
        headline: typeof rule.line === 'function' ? rule.line(resolved) : rule.line,
        headlineKey: rule.key,
        renewableShare: sumShares(resolved.mix, RENEWABLE_FUELS),
        zeroCarbonShare: sumShares(resolved.mix, ZERO_CARBON_FUELS),
        windShare: shareOf(resolved.mix, 'wind'),
        solarShare: shareOf(resolved.mix, 'solar'),
        coalFree: shareOf(resolved.mix, 'coal') === 0,
    };
}
